import React, { useEffect, useMemo, useRef, useState } from "react";
import JobCodePicker from "./JobCodePicker";
import "./ClockOutJobCodeSheet.css";

/**
 * ClockOutJobCodeSheet — clock-out job-code confirm/change bottom sheet.
 *
 * TIME-006-V2 (clockout-prompt). Surfaces between the user tapping "Clock Out"
 * on `ClockInOutScreen` and the actual clock-out mutation, when the open
 * TimeLog has a `jobCode` set. Pre-selects the punch-in code; staff confirms
 * (one tap) or changes the selection then confirms.
 *
 * Authoritative spec: docs/design/TIME-006-V2-design.md §2-§12.
 * Pairs with: docs/architecture/TIME-006-V2-tech.md (Marcus, Option C).
 *
 * Behaviour (matches v1 ClockInJobCodeSheet for pattern continuity):
 *   - Open: focus moves to the picker input; Tab cycles to Cancel/Confirm;
 *     Esc dismisses (treated as Cancel — design spec §11 + AC Scenario 4).
 *   - Backdrop click dismisses without firing the mutation.
 *   - Confirm fires `onConfirm(jobCode)` with the currently-selected code.
 *     The host (`ClockInOutScreen`) is responsible for mapping "no change"
 *     vs "change" semantics — this sheet always emits the selected code, and
 *     the host omits the body field when it equals the punch-in value (so the
 *     backend's no-mutation no-op path is taken, per tech doc §3.1).
 *   - Reduced-motion: slide-up animation suppressed via CSS @media block.
 *
 * Stale-code handling: when the punch-in code is archived mid-shift (no
 * longer in the active list), the host passes the punch-in code via
 * `staleCode` so the picker can render it with an "(inactive)" suffix per
 * v1 §3.1 stale-code rule (design spec §3.5 + AC Scenario 5).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Array}   props.codes        — the cached active job-code list
 * @param {string}  props.punchInJobCode  — the TimeLog's current `jobCode`
 *                                       (pre-selected default)
 * @param {string}  [props.punchInDisplayName] — display label for the helper
 *                                       text ("You punched in as ..."). When
 *                                       absent, the punch-in code itself is
 *                                       echoed.
 * @param {boolean} props.submitting   — disable interactions during the
 *                                       in-flight clock-out mutation
 * @param {string}  [props.errorMessage] — surfaced via JobCodePicker
 *                                       errorMessage prop on backend reject
 *                                       (design spec §States Summary "Error")
 * @param {(code: string) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const ClockOutJobCodeSheet = ({
  open,
  codes = [],
  punchInJobCode = null,
  punchInDisplayName = null,
  submitting = false,
  errorMessage = null,
  onConfirm,
  onCancel,
}) => {
  const [pendingCode, setPendingCode] = useState(punchInJobCode);
  const headingId = useMemo(
    () => `cojcs-heading-${Math.random().toString(36).slice(2, 9)}`,
    []
  );
  const helperId = `${headingId}-helper`;
  const containerRef = useRef(null);

  // Reset selection to the punch-in code each time the sheet opens. This
  // gives staff a confirm-by-default one-tap path (AC Scenario 1).
  useEffect(() => {
    if (open) {
      setPendingCode(punchInJobCode);
    }
  }, [open, punchInJobCode]);

  // Esc handler — treats Esc as Cancel = abort clock-out (design spec §11
  // + backlog §AC Scenario 4: dismissal aborts; no API call fires).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        if (typeof onCancel === "function") onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  // Helper text label: prefer the explicit display name when the host has
  // resolved one from the active codes list; fall back to the raw code if
  // not. Avoids "You punched in as null" when the punch-in row had no code.
  const punchInLabel = punchInDisplayName || punchInJobCode || "";

  // The picker reuses v1; staleCode lets it render the punch-in code as
  // selectable with an "(inactive)" suffix even if archived mid-shift
  // (design spec §3.5 + AC Scenario 5). Cheap: pass punchInJobCode through
  // unconditionally — the picker itself only adds the inactive item when
  // the code is NOT in the active list.
  const handleBackdropClick = () => {
    if (submitting) return;
    if (typeof onCancel === "function") onCancel();
  };

  const handleConfirm = () => {
    if (submitting) return;
    if (typeof onConfirm === "function") {
      // pendingCode falls back to punchInJobCode on first render (the
      // useEffect above seeds it). If the picker was cleared somehow, we
      // still confirm with the punch-in code — the design spec §3.5
      // requires `required={true}` on this host so a null-emit is a
      // defensive fallback that should never fire in practice.
      onConfirm(pendingCode || punchInJobCode);
    }
  };

  return (
    <div
      className="cojcs-backdrop"
      role="presentation"
      onMouseDown={handleBackdropClick}
      data-testid="clock-out-job-code-sheet-backdrop"
    >
      <div
        ref={containerRef}
        className="cojcs-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={helperId}
        // Stop the backdrop click handler when the user interacts inside the sheet.
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="clock-out-job-code-sheet"
      >
        <h2 id={headingId} className="cojcs-heading">
          Confirm your job for this shift
        </h2>
        <p id={helperId} className="cojcs-helper">
          {punchInLabel
            ? `You punched in as ${punchInLabel}. Confirm or change before you clock out.`
            : "Confirm or change your job before you clock out."}
        </p>
        <div className="cojcs-picker-slot">
          <label className="cojcs-field-label" htmlFor="cojcs-picker-input">
            Job code
          </label>
          <JobCodePicker
            testId="clock-out-job-code-picker"
            inputId="cojcs-picker-input"
            codes={codes}
            value={pendingCode}
            onChange={(code) => setPendingCode(code || null)}
            placeholder="Pick a job code..."
            emptyState="disabled-pill"
            required
            staleCode={punchInJobCode || null}
            errorMessage={errorMessage || undefined}
            disabled={submitting}
          />
        </div>
        <div className="cojcs-footer">
          <button
            type="button"
            className="cojcs-btn cojcs-btn--secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="clock-out-job-code-sheet-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="cojcs-btn cojcs-btn--primary"
            onClick={handleConfirm}
            disabled={submitting}
            aria-busy={submitting || undefined}
            data-testid="clock-out-job-code-sheet-confirm"
          >
            {submitting ? "Clocking out..." : "Clock out"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockOutJobCodeSheet;
